import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import clsx from 'classnames';
import { Box, Drawer, DrawerProps, IconButton, List, ListItem, makeStyles } from '@material-ui/core';
import { MDSTheme, MDSTypography } from '@marqvision/mds/core';
import { EditIcon, DocumentOutlineIcon, ProtectionIcon, CloseDeleteRemoveOutlineIcon } from '@marqvision/mds/icons';
import { useLanguage, useGlobalAction, useBreakpoint, useRouteNames } from '~/hooks';
import { useAuth } from '../../apis/auth';
import bg from '../../images/background.jpg';
import Footer from '../Footer';
import { Logger } from '../../utils';
import { latestScrollY } from '../../atoms/myApp';
import { useGetTmNew } from '../../apis/trademarks';
import Control from './Control';
import MenuButton from './DrawerButton';

export const DrawerWidthForDesktop = '341px';
export const DrawerWidthForMobile = '360px';

interface IProps {
  variant?: 'normal' | 'light';
  DrawerProps?: DrawerProps;
  onClose: () => void;
}

const useStyles = makeStyles(() => ({
  drawer: {
    width: DrawerWidthForDesktop,
    flexShrink: 0,
  },
  drawerLight: {
    width: '320px',
  },
  drawerPaper: {
    width: DrawerWidthForDesktop,
    padding: '40px 0 40px 40px',
    background: `url(${bg}) no-repeat center center`,
    backgroundSize: 'cover',
  },
  drawerPaperLight: {
    padding: '0 0 24px 0',
    width: '100%',
    maxWidth: '320px',
    background: 'white',
  },
  list: { padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' },
  listItem: {
    padding: '0',
  },
  link: { textDecoration: 'none', width: '100%' },
  tag: {
    marginLeft: '4px',
    display: 'inline-block',
    padding: '1px 4px',
    borderRadius: '4px',
    backgroundColor: MDSTheme.palette.red[600],
  },
}));

const Sidemenu = ({ variant = 'normal', DrawerProps, onClose }: IProps): JSX.Element => {
  const RouteNames = useRouteNames();
  const classes = useStyles();

  const { Lang } = useLanguage();
  const { pathname } = useLocation();
  const { data: authData } = useAuth();
  const { login } = useGlobalAction();
  const { getMediaQueryValue: mq } = useBreakpoint();
  const navigate = useNavigate();
  const setScrollY = useSetAtom(latestScrollY);

  const { data } = useGetTmNew();

  const matchFileAnApplication = pathname.indexOf(RouteNames.FileAnApplication) === 0;
  const matchMyApplication = pathname.indexOf(RouteNames.MyApplication) === 0;
  const matchTrademark = pathname.indexOf(RouteNames.TrademarkManagement) === 0;
  const matchWatch = pathname.indexOf(RouteNames.Watch) === 0;
  const matchGlobalStatus = pathname.indexOf(RouteNames.GlobalStatus) === 0;

  const getIconColor = (urlMatched: boolean) =>
    urlMatched
      ? MDSTheme.palette.blue[700]
      : variant === 'light'
      ? MDSTheme.palette.bluegray[800]
      : MDSTheme.palette.white;

  const iconScale = (() => (variant === 'light' ? 0.8333 : 0.8))();

  return (
    <Drawer
      className={classes.drawer}
      variant="permanent"
      classes={{
        paper: clsx(classes.drawerPaper, { [classes.drawerPaperLight]: variant === 'light' }),
      }}
      anchor="left"
      {...DrawerProps}
    >
      <Box height="56px" display="flex" alignItems="center">
        <IconButton style={{ marginLeft: '12px', padding: 0 }} onClick={onClose}>
          <CloseDeleteRemoveOutlineIcon color={MDSTheme.palette.bluegray[800]} scale={1} />
        </IconButton>
        <Control size="medium" onLoginClick={() => onClose()} notification />
      </Box>

      {/* Menus */}
      <Box flex="1" padding="0 12px">
        <List className={classes.list}>
          <ListItem className={classes.listItem}>
            <Link
              to={RouteNames.FileAnApplication}
              className={classes.link}
              onClick={() => {
                onClose();
                Logger.track('clicked file an application', {
                  Through: 'LNB-File an application',
                });
              }}
            >
              <MenuButton
                variant={variant}
                selected={matchFileAnApplication}
                startIcon={<EditIcon color={getIconColor(matchFileAnApplication)} scale={iconScale} />}
              >
                {Lang.FileAnApplication}
              </MenuButton>
            </Link>
          </ListItem>
          <ListItem className={classes.listItem}>
            <Link
              to={RouteNames.MyApplication}
              className={classes.link}
              onClick={(e) => {
                onClose();
                setScrollY(0);
                Logger.track('clicked my application');
                if (!authData?.accessToken) {
                  e.preventDefault();
                  login(() => navigate(RouteNames.MyApplication));
                }
              }}
            >
              <MenuButton
                variant={variant}
                selected={matchMyApplication}
                startIcon={<DocumentOutlineIcon color={getIconColor(matchMyApplication)} scale={iconScale} />}
              >
                {Lang.MyApplication}
              </MenuButton>
            </Link>
          </ListItem>
          <MDSTypography
            variant="T14"
            weight="medium"
            style={{ height: '32px', paddingLeft: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}
          >
            <ProtectionIcon scale={iconScale} />
            {Lang.IpManagement}
          </MDSTypography>
          <div style={{ paddingLeft: '12px' }}>
            <ListItem className={classes.listItem}>
              <Link
                to={RouteNames.GlobalStatus}
                className={classes.link}
                onClick={(e) => {
                  onClose();
                  Logger.track('clicked global ip status');
                  if (!authData?.accessToken) {
                    e.preventDefault();
                    login(() => navigate(RouteNames.GlobalStatus));
                  }
                }}
              >
                <MenuButton
                  variant={variant}
                  selected={matchGlobalStatus}
                  // startIcon={<ProtectionIcon color={getIconColor(matchGlobalStatus)} scale={iconScale} />}
                >
                  {Lang.trademarks.GlobalIpStatus}
                </MenuButton>
              </Link>
            </ListItem>
            <ListItem className={classes.listItem}>
              <Link
                to={RouteNames.Watch}
                className={classes.link}
                onClick={(e) => {
                  onClose();
                  Logger.track('clicked watch');
                }}
              >
                <MenuButton
                  variant={variant}
                  selected={matchWatch}
                  // startIcon={<ProtectionIcon color={getIconColor(matchWatch)} scale={iconScale} />}
                >
                  {Lang.Watch}
                </MenuButton>
              </Link>
            </ListItem>
            <ListItem className={classes.listItem}>
              <Link
                to={RouteNames.TrademarkManagement}
                className={classes.link}
                onClick={(e) => {
                  onClose();
                  Logger.track('clicked trademark management');
                }}
              >
                <MenuButton
                  variant={variant}
                  selected={matchTrademark}
                  // startIcon={<ProtectionIcon color={getIconColor(matchTrademark)} scale={iconScale} />}
                >
                  {Lang.trademarks.Trademarks}
                  {data?.hasNewIpTmRegistration && (
                    <MDSTypography className={classes.tag} variant="T12" weight="medium" color={MDSTheme.palette.white}>
                      New
                    </MDSTypography>
                  )}
                </MenuButton>
              </Link>
            </ListItem>
          </div>
        </List>
      </Box>

      <Footer variant="light" />
    </Drawer>
  );
};

export default Sidemenu;
